import { useEffect, useMemo, useState } from "react";

import { isToday } from "date-fns";
import DateTimePickerModal from "react-native-modal-datetime-picker";

import { DateFormat } from "@/constants/DateFormat";
import { InputFieldBaseStyles } from "@/constants/UI";
import DatePickerBottomSheetModal from "@/ui/composed/pickers/DatePickerBottomSheetModal";
import Button from "@/ui/core/buttons/Button";
import Icon, { FAIconName } from "@/ui/core/Icon";
import Text from "@/ui/core/Text";
import { Box, HStack } from "@/ui/layout";
import { ColorToken, ColorTokenValues } from "@/ui/theme/tokens";
import { formatDate } from "@/utils/date.util";
import { isAndroid } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface DateAndTimeProps extends ViewWithTestID {
  date: Date;
  onPress: () => void;
}

const DateAndTime = ({ date, onPress, testID }: DateAndTimeProps) => {
  const displayDate = isToday(date)
    ? "Today"
    : formatDate({ date, format: DateFormat.shortMonthDay });
  const displayTime = formatDate({ date, format: DateFormat.time });
  return (
    <HStack alignItems="center" gap={10} paddingRight={21}>
      <Text testID={joinTestIDs(testID, "displayText")}>
        {displayDate} at {displayTime}
      </Text>
      <Button
        leftIcon={FAIconName.Pen}
        onPress={onPress}
        size="small"
        testID={joinTestIDs(testID, "edit")}
        title="Edit"
        variant="tertiary"
      />
    </HStack>
  );
};

const getPickerProps = (mode: Exclude<DateTimePickerMode, "datetime">) => {
  if (mode === "date") {
    return {
      format: DateFormat.shortNumberMonthDayYear,
      icon: FAIconName.Calendar,
    };
  }
  return {
    format: DateFormat.time,
    icon: FAIconName.Clock,
  };
};

interface OnlyDateOrOnlyTimeProps extends ViewWithTestID {
  date: Date;
  mode: Exclude<DateTimePickerMode, "datetime">;
  onPress: () => void;
}

const OnlyDateOrOnlyTime = ({
  date,
  mode,
  onPress,
  testID,
}: OnlyDateOrOnlyTimeProps) => {
  const { format, icon } = getPickerProps(mode);
  return (
    <Box onPress={onPress}>
      <HStack alignItems="center" gap={6}>
        <Icon name={icon} size="md" testID={testID} />
        <Text testID={joinTestIDs(testID, "text")} variant="body">
          {formatDate({ date, format })}
        </Text>
      </HStack>
    </Box>
  );
};

interface DateTimePickerProps extends ViewWithTestID {
  date?: Date;
  isCurrentDateMaxDate?: boolean;
  isDisabled?: boolean;
  mode: DateTimePickerMode;
  onChange: (date: Date) => void;
}

/* eslint complexity: ["error", { max: 12 }] -- DateTimePicker has conditional logic for read-only mode, datetime vs date/time modes, and Android vs iOS */
const DateTimePicker = ({
  date: dateProp,
  isCurrentDateMaxDate,
  isDisabled = false,
  mode,
  onChange,
  testID,
}: DateTimePickerProps) => {
  const date = useMemo(() => dateProp || new Date(), [dateProp]);
  const [isVisible, setIsVisible] = useState(false);

  const [maxDate, setMaxDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    if (isVisible && isCurrentDateMaxDate) {
      setMaxDate(new Date());
    }
  }, [isVisible, isCurrentDateMaxDate]);

  const handlePress = () => {
    if (isDisabled) return;
    setIsVisible(true);
  };
  const handleChange = (selectedDate: Date) => {
    onChange(selectedDate);
    setIsVisible(false);
  };

  const baseStyles = mode === "datetime" ? {} : InputFieldBaseStyles;

  const shouldRenderModal = isVisible && !isDisabled;
  return (
    <Box
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...baseStyles}
      backgroundColor={
        isDisabled
          ? ColorTokenValues[ColorToken.NeutralBackgroundSecondary]
          : ColorTokenValues[ColorToken.NeutralBackgroundPrimary]
      }
      borderColor={
        isVisible ? ColorToken.Primary100 : ColorToken.NeutralTertiary
      }
      flexGrow={1}
      flexShrink={1}
      testID={testID}
    >
      {mode === "datetime" ? (
        <DateAndTime date={date} onPress={handlePress} testID={testID} />
      ) : (
        <OnlyDateOrOnlyTime
          date={date}
          mode={mode}
          onPress={handlePress}
          testID={testID}
        />
      )}
      {isAndroid() ? (
        <DateTimePickerModal
          date={date}
          isVisible={shouldRenderModal}
          mode={mode}
          onCancel={() => setIsVisible(false)}
          onConfirm={handleChange}
        />
      ) : (
        <DatePickerBottomSheetModal
          date={date}
          isOpen={shouldRenderModal}
          maxDate={maxDate}
          mode={mode}
          onChange={handleChange}
          onRequestClose={() => setIsVisible(false)}
          testID={testID}
        />
      )}
    </Box>
  );
};

export default DateTimePicker;
export type { DateTimePickerProps };
